import { AccountTypeGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  AdminAgentPropertyMap,
  BackupServicerPropertyMap,
  BaseFundingRequestComponentPropertyMap,
  BorrowerPropertyMap,
  BorrowingBaseMechanicsPropertyMap,
  CapitalProviderPropertyMap,
  CapitalProviderRepresentativePropertyMap,
  ClassMechanicsPropertyMap,
  CollectionAccountBankPropertyMap,
  CollectionsPaydownPropertyMap,
  CollectionsRecyclePropertyMap,
  CurePaydownPropertyMap,
  DrawRequestPropertyMap,
  FacilityMechanicsPropertyMap,
  FundingRequestGeneralPropertyMap,
  FundingRequestMechanicsPropertyMap,
  PartiesTypeMap,
  PayingAgentPropertyMap,
  PropertyTypeContainerToPropertyTypeMap,
  ServicerPropertyMap,
  SettlementMechanicsPropertyMap,
  VerificationAgentPropertyMap,
  CreditAgreement,
} from './types';

export const propertyTypeContainerToPropertyMap: PropertyTypeContainerToPropertyTypeMap = {
  deliverableReportPropertyContainer:
    CreditAgreement.DomainEntity.PropertyType.DeliverableReportProperty,
  linkedAccountPropertyContainer:
    CreditAgreement.DomainEntity.PropertyType.LinkedAccountProperty,
  reportTypePropertyContainer:
    CreditAgreement.DomainEntity.PropertyType.ReportTypeProperty,
  textPropertyContainer: CreditAgreement.DomainEntity.PropertyType.TextProperty,
};

const adminAgentPropertyMap: AdminAgentPropertyMap = {
  feeDescription: CreditAgreement.DomainEntity.PropertyType.TextProperty,
};

const borrowerPropertyMap: BorrowerPropertyMap = {
  borrowerEntityName: CreditAgreement.DomainEntity.PropertyType.TextProperty,
};

const backupServicerPropertyMap: BackupServicerPropertyMap = {};

const capitalProviderPropertyMap: CapitalProviderPropertyMap = {
  applicableMarginBeginning:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  callProtectionValue:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  commitmentFee:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  commitmentAmount: CreditAgreement.DomainEntity.PropertyType.CurrencyProperty,
  interestRateBenchmark:
    CreditAgreement.DomainEntity.PropertyType.InterestRateBenchmarkProperty,
  interestRateBenchmarkCeiling:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  interestRateBenchmarkDeterminationDaysOffset:
    CreditAgreement.DomainEntity.PropertyType.NumberProperty,
  interestRateBenchmarkFloor:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  interestRateBenchmarkPeriod:
    CreditAgreement.DomainEntity.PropertyType.FrequencyPeriodProperty,
  interestRateFixedBase:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  unusedFee: CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  upFrontFee: CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  extensionFee:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  amortizationPeriodDrawnFee:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  overcollateralizationAmount:
    CreditAgreement.DomainEntity.PropertyType
      .OvercollateralizationAmountProperty,
  allowAccessToDataFromAllReportingDates:
    CreditAgreement.DomainEntity.PropertyType.BooleanProperty,
};

const capitalProviderRepresentativePropertyMap: CapitalProviderRepresentativePropertyMap = {};

const collectionAccountBankPropertyMap: CollectionAccountBankPropertyMap = {};

const payingAgentPropertyMap: PayingAgentPropertyMap = {};

const servicerPropertyMap: ServicerPropertyMap = {
  feeDescription: CreditAgreement.DomainEntity.PropertyType.TextProperty,
  feePaymentDateDescription:
    CreditAgreement.DomainEntity.PropertyType.TextProperty,
};

const verificationAgentPropertyMap: VerificationAgentPropertyMap = {};

const facilityMechanicsPropertyMap: FacilityMechanicsPropertyMap = {
  closingDate: CreditAgreement.DomainEntity.PropertyType.DateProperty,
  finalMaturityDate: CreditAgreement.DomainEntity.PropertyType.DateProperty,
  amortizationPeriodStart:
    CreditAgreement.DomainEntity.PropertyType.DateProperty,
  amortizationPeriodEnd: CreditAgreement.DomainEntity.PropertyType.DateProperty,
  callProtectionExpirationDate:
    CreditAgreement.DomainEntity.PropertyType.DateProperty,
  callProtectionValue:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  commitmentPeriodEffectiveDate:
    CreditAgreement.DomainEntity.PropertyType.DateProperty,
  externalFacilityId: CreditAgreement.DomainEntity.PropertyType.TextProperty,
  interestRateCeiling:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  governingLaw: CreditAgreement.DomainEntity.PropertyType.TextProperty,
  minimumUtilization:
    CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty,
  revolvingCommitmentTermination:
    CreditAgreement.DomainEntity.PropertyType.DateProperty,
  seniority: CreditAgreement.DomainEntity.PropertyType.TextProperty,
  yieldMaintenance: CreditAgreement.DomainEntity.PropertyType.DateProperty,
  parties: {
    // @ts-ignore
    adminAgents: adminAgentPropertyMap,
    backupServicers: backupServicerPropertyMap,
    borrowers: borrowerPropertyMap,
    capitalProviders: capitalProviderPropertyMap,
    capitalProviderRepresentatives: capitalProviderRepresentativePropertyMap,
    collectionAccountBanks: collectionAccountBankPropertyMap,
    payingAgents: payingAgentPropertyMap,
    servicers: servicerPropertyMap,
    verificationAgents: verificationAgentPropertyMap,
  },
};

const classMechanicsPropertyMap: ClassMechanicsPropertyMap = {};

// @ts-ignore
const borrowingBaseMechanicsPropertyMap: BorrowingBaseMechanicsPropertyMap = {
  reportingDateDescription:
    CreditAgreement.DomainEntity.PropertyType.TextProperty,
  concentrationLimitTransitionDate:
    CreditAgreement.DomainEntity.PropertyType.DateProperty,
};

const baseFundingRequestComponentPropertyMap: BaseFundingRequestComponentPropertyMap = {
  minimumAmount: CreditAgreement.DomainEntity.PropertyType.CurrencyProperty,
  incrementAmount: CreditAgreement.DomainEntity.PropertyType.CurrencyProperty,
  maximumFrequency: CreditAgreement.DomainEntity.PropertyType.FrequencyProperty,
  submissionNoticePeriod:
    CreditAgreement.DomainEntity.PropertyType.NumberProperty,
  requiredDocumentation:
    CreditAgreement.DomainEntity.PropertyContainerType
      .ReportTypePropertyContainer,
  sourceAccounts:
    CreditAgreement.DomainEntity.PropertyContainerType
      .LinkedAccountPropertyContainer,
  destinationAccounts:
    CreditAgreement.DomainEntity.PropertyContainerType
      .LinkedAccountPropertyContainer,
};

const fundingRequestGeneralPropertyMap: FundingRequestGeneralPropertyMap = {
  autoApproveFundingRequests:
    CreditAgreement.DomainEntity.PropertyType.BooleanProperty,
  autoApprove: CreditAgreement.DomainEntity.PropertyType.AutoApprovalProperty,
  autoSettleHour: CreditAgreement.DomainEntity.PropertyType.NumberProperty,
  expireDaysOffset: CreditAgreement.DomainEntity.PropertyType.NumberProperty,
  overcollateralizationMethod:
    CreditAgreement.DomainEntity.PropertyType
      .OvercollateralizationMethodProperty,
  maximumBorrowingBaseReportDaysOld:
    CreditAgreement.DomainEntity.PropertyType.NumberProperty,
  maximumPrefundingAmount:
    CreditAgreement.DomainEntity.PropertyType.MaximumPrefundingAmountProperty,
  programmaticFundingDates:
    CreditAgreement.DomainEntity.PropertyType.RecurringDateProperty,
};
const collectionsRecyclePropertyMap: CollectionsRecyclePropertyMap = {
  isAllowed: CreditAgreement.DomainEntity.PropertyType.BooleanProperty,
  limitToNewlyAssignedEligibleExposureSinceLastApprovedFundingRequest:
    CreditAgreement.DomainEntity.PropertyType.BooleanProperty,
  ...baseFundingRequestComponentPropertyMap,
};
const collectionsPaydownPropertyMap: CollectionsPaydownPropertyMap = {
  classAllocationMethod:
    CreditAgreement.DomainEntity.PropertyType.ClassAllocationMethodProperty,
  isAllowed: CreditAgreement.DomainEntity.PropertyType.BooleanProperty,
  isMaxPaydownOverrideAllowed:
    CreditAgreement.DomainEntity.PropertyType.BooleanProperty,
  ...baseFundingRequestComponentPropertyMap,
};
const curePaydownPropertyMap: CurePaydownPropertyMap = {
  classAllocationMethod:
    CreditAgreement.DomainEntity.PropertyType.ClassAllocationMethodProperty,
  ...baseFundingRequestComponentPropertyMap,
};
const drawRequestPropertyMap: DrawRequestPropertyMap = {
  classAllocationMethod:
    CreditAgreement.DomainEntity.PropertyType.ClassAllocationMethodProperty,
  isStopDrawEnforced: CreditAgreement.DomainEntity.PropertyType.BooleanProperty,
  maximumAmountPerPeriod:
    CreditAgreement.DomainEntity.PropertyType.CurrencyProperty,
  maximumAmountPeriod:
    CreditAgreement.DomainEntity.PropertyType.FrequencyPeriodProperty,
  ...baseFundingRequestComponentPropertyMap,
};

const fundingRequestMechanicsPropertyMap: FundingRequestMechanicsPropertyMap = {
  properties: fundingRequestGeneralPropertyMap,
  collectionsPaydown: collectionsPaydownPropertyMap,
  collectionsRecycle: collectionsRecyclePropertyMap,
  curePaydown: curePaydownPropertyMap,
  drawRequest: drawRequestPropertyMap,
};

const settlementMechanicsPropertyMap: SettlementMechanicsPropertyMap = {
  settlementDates:
    CreditAgreement.DomainEntity.PropertyType.RecurringDateProperty,
  settlementDateDescription:
    CreditAgreement.DomainEntity.PropertyType.TextProperty,
  servicerCollectionsSweep:
    CreditAgreement.DomainEntity.PropertyType.TextProperty,
  settlementDateWaterfallPriority:
    CreditAgreement.DomainEntity.PropertyContainerType.TextPropertyContainer,
  interestPaymentDateDescription:
    CreditAgreement.DomainEntity.PropertyType.TextProperty,
};

const partiesTypeMap: PartiesTypeMap = {
  // @ts-ignore
  adminAgents: CreditAgreement.DomainEntity.Items.PartyType.AdminAgent,
  backupServicers: CreditAgreement.DomainEntity.Items.PartyType.BackupServicer,
  borrowers: CreditAgreement.DomainEntity.Items.PartyType.Borrower,
  capitalProviders:
    CreditAgreement.DomainEntity.Items.PartyType.CapitalProvider,
  capitalProviderRepresentatives:
    CreditAgreement.DomainEntity.Items.PartyType.CapitalProviderRepresentative,
  collectionAccountBanks:
    CreditAgreement.DomainEntity.Items.PartyType.CollectionAccountBank,
  payingAgents: CreditAgreement.DomainEntity.Items.PartyType.PayingAgent,
  servicers: CreditAgreement.DomainEntity.Items.PartyType.Servicer,
  verificationAgents:
    CreditAgreement.DomainEntity.Items.PartyType.VerificationAgent,
};

export const creditAgreementFullArtifactPartyMap = {
  facilityMechanics: {
    parties: partiesTypeMap,
  },
};

export const creditAgreementFullArtifactPropertyMap: {
  [key in keyof Omit<
    CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
    | 'id'
    | 'creditFacilityId'
    | 'facilityType'
    | 'effectiveFrom'
    | 'effectiveTo'
    | 'version'
    | '_schemaVersion_'
    | 's3Path'
    | 'financialCovenants'
    | 'portfolioCovenants'
    | 'reportingDeliverables' // omit from property map because has own rendering table
    | 'deliverables'
    | 'reportingMechanics'
  >]:
    | FacilityMechanicsPropertyMap
    | ClassMechanicsPropertyMap
    | FundingRequestMechanicsPropertyMap
    | BorrowingBaseMechanicsPropertyMap
    | SettlementMechanicsPropertyMap;
} = {
  classMechanics: classMechanicsPropertyMap,
  facilityMechanics: facilityMechanicsPropertyMap,
  fundingRequestMechanics: fundingRequestMechanicsPropertyMap,
  borrowingBaseMechanics: borrowingBaseMechanicsPropertyMap,
  settlementMechanics: settlementMechanicsPropertyMap,
};

export const CaWipExitPromptMessage = `Make sure to save any changes you want to keep. To save changes, click the Save Progress button at the top of the page.\n\nAre you sure you want to leave?`;

export const borrowingBaseContributionAccountTypes: AccountTypeGQLType[] = [
  'collections',
  'preFundingAccount',
  'permittedInvestmentsAccount',
  'accountIncludedInBorrowingBase',
];

export const bankAccountTypes: AccountTypeGQLType[] = [
  'capitalProviderFundingAccount',
  'collections',
  'otherReserveAccount',
  'parentOperatingAccount',
  'preFundingAccount',
  'permittedInvestmentsAccount',
  'accountIncludedInBorrowingBase',
];
